import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TFunction } from 'i18next';
import { useCountry, useCurrency, useEventTracker } from '@increasecard/increase-core';
import {
  CountryFilter,
  ErrorMessage,
  NumberBox,
  GetFiltersResponse,
  getDateRangeFiltersFormatted
} from '@card/commonlib';

import { Field, SelectOption } from '@increasecard/typed-components/dist/types/Field';
import { Filter, FilterButton, DateRangeInput } from '@increasecard/typed-components';

import { ContentCard, ContentWrapper } from '../../common/components/ContentCard';
import OperationsList from './components/OperationsList';
import { useShallowEqualSelector } from '../../utils/reduxUtils';
import { CreateReportButton } from '../../common/components/report/CreateReportButton';
import OperationsActions from './actions';
import FiltersActions from '../../actions/filtersActions';
import ShopsActions from '../../actions/shopsActions';
import { CurrencyTabs } from '../../common/components/CurrencyTabs';
import { toCapitalize } from '../../utils/stringUtils';
import Constants from '../../constants';
import {
  filtersToParams,
  shopsToOptions,
  infoProvidersToOptions,
  compareOptionsByName
} from '../../utils/filtersUtils';
import { ActionButtons, ActionHeader, FilterGroup } from '../../common/components/ActionHeader';
import { Currency } from '@card/types';
import DateTypeSelector from './components/DateTypeSelector';

const buildFields = (
  filters: GetFiltersResponse,
  country: string,
  shops: SelectOption[],
  translationFunction: TFunction
): Field[] => {
  return [
    {
      name: 'shop_id',
      displayName: translationFunction('common:filters:shops:displayName'),
      type: 'selectMultiple',
      options: shops.sort(compareOptionsByName),
      hasSearch: true,
      hasClearButton: true
    },
    {
      name: 'card_brand',
      displayName: translationFunction('common:filters:cardBrand:displayName'),
      type: 'select',
      options: [
        { value: '', name: translationFunction('common:filters:cardBrand:selectAll') },
        ...filters.card_brands.map((cardBrand) => {
          return {
            value: cardBrand,
            name: toCapitalize(cardBrand)
          };
        })
      ]
    },
    {
      name: 'info_provider',
      displayName: translationFunction('common:filters:processor:displayName'),
      type: 'select',
      options: [
        { value: '', name: translationFunction('common:filters:processor:selectAll') },
        ...infoProvidersToOptions(filters.current_processor_names, translationFunction)
      ]
    },
    {
      name: 'voucher',
      displayName: translationFunction('common:filters:voucher:displayName'),
      type: 'text',
      config: {
        enabledKinds: {
          equals: true,
          contains: false
        }
      }
    },
    {
      name: 'batch',
      displayName: translationFunction('common:filters:batch:displayName'),
      type: 'text',
      config: {
        enabledKinds: {
          equals: true,
          contains: false
        }
      }
    },
    {
      name: 'amount',
      displayName: translationFunction('common:filters:amount:displayName'),
      type: 'number'
    }
  ];
};

const TotalWrapper = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
`;

const OperationsScreen = () => {
  const dispatch = useDispatch();
  const tracker = useEventTracker();
  const { t } = useTranslation();
  const [currentFilters, setCurrentFilters] = useState<Record<string, Filter>>({});
  const [fields, setFields] = useState<Field[]>([]);
  const [dateRangeInput, setDateRangeInput] = useState([new Date(), new Date()]);
  const [byPayDate, setByPayDate] = useState(true);
  const currency = useCurrency();
  const [currentCurrency, setCurrentCurrency] = useState<Currency>(currency || 'ARS');
  const { operations, filters, shops } = useShallowEqualSelector((state) => ({
    operations: state.operations,
    filters: state.filters,
    shops: state.shops
  }));
  const country = useCountry();

  const filtersFormatted = useMemo(
    () => ({
      ...filtersToParams(currentFilters),
      daterange: getDateRangeFiltersFormatted(dateRangeInput),
      currency: currentCurrency,
      // eslint-disable-next-line @typescript-eslint/camelcase
      by_pay_date: byPayDate.toString()
    }),
    [currentFilters, dateRangeInput, currentCurrency, byPayDate]
  );

  const loadPage = useCallback(
    (page: number) => {
      dispatch(
        OperationsActions.fetch({
          page: page,
          filters: filtersFormatted
        })
      );
    },
    [dispatch, filtersFormatted]
  );

  useEffect(() => {
    if (shops.shopsList.length === 0) dispatch(ShopsActions.fetch());
  }, [shops.shopsList, dispatch]);

  useEffect(() => {
    if (!filters.list.data) dispatch(FiltersActions.fetch());
  }, [filters.list.data, dispatch]);

  useEffect(() => {
    if (filters.list.data && country && fields.length === 0 && shops.shopsList.length > 0) {
      setFields(buildFields(filters.list.data, country, shopsToOptions(shops.shopsList), t));
    }
  }, [shops.shopsList, filters.list.data, dispatch, country, t, fields.length]);

  useEffect(() => {
    loadPage(Constants.FIRST_PAGE);
  }, [loadPage]);

  const onErrorMessageClick = () => loadPage(operations.list.settings.current_page);

  if (operations.list.error) {
    return (
      <ContentCard>
        <ContentWrapper>
          <ErrorMessage onErrorMessageClick={onErrorMessageClick}>
            {t('api:errors:default')}
          </ErrorMessage>
        </ContentWrapper>
      </ContentCard>
    );
  }

  const handleCreateReport = () => {
    // eslint-disable-next-line @typescript-eslint/camelcase
    tracker.track('Generate report', { report_type: 'operations' });
    dispatch(
      OperationsActions.generateReport({
        page: operations.list.settings.current_page,
        filters: filtersFormatted
      })
    );
  };

  return (
    <ContentCard>
      {/* TODO (Francossegura): The condition should be that the country has two or more currencies */}
      <CountryFilter
        hideFor={['argentina', 'ecuador', 'dominicana', 'chile', 'colombia', 'paraguay']}
      >
        <CurrencyTabs onChange={setCurrentCurrency} value={currentCurrency} />
      </CountryFilter>
      <ContentWrapper>
        <>
          <ActionHeader>
            <FilterGroup>
              <DateRangeInput
                id="operationsDateRangeInput"
                label=""
                labelHidden
                onChange={setDateRangeInput}
                style={{ width: 280 }}
                value={dateRangeInput}
              />
              <DateTypeSelector byPayDate={byPayDate} onChangeByPayDate={setByPayDate} />
              <FilterButton fields={fields} onChange={setCurrentFilters} value={currentFilters} />
            </FilterGroup>
            <ActionButtons>
              <CreateReportButton
                onCreateReport={handleCreateReport}
                targetText={t('operations:operations')}
                translationFunction={t}
              />
            </ActionButtons>
          </ActionHeader>
          <TotalWrapper>
            <NumberBox
              currency={currentCurrency as Currency}
              title={t('operations:totalBox:description')}
              value={operations.list.fetching ? undefined : operations.list.settings.total}
              variant="light70"
            />
          </TotalWrapper>
          <OperationsList
            currentPage={operations.list.settings.current_page}
            fetchingData={operations.list.fetching}
            operations={operations.list.data}
            selectPage={loadPage}
            totalPages={operations.list.settings.total_pages}
          />
        </>
      </ContentWrapper>
    </ContentCard>
  );
};

export default OperationsScreen;
